/* eslint-disable no-underscore-dangle */
import { h, render } from 'preact';
import { algoliasearch } from 'algoliasearch';
import instantsearch from 'instantsearch.js';
import { connectSearchBox } from 'instantsearch.js/es/connectors';
import { insightsClient } from '@algolia/client-insights';
import * as widgets from 'instantsearch.js/es/widgets';
import routing from './search_routing';
import { appId, apiKey, indexName } from './config';
import * as templates from './templates';
import { gql } from '../shopify/api';
import { QUERY_PRODUCTS_BY_IDS } from '../shopify/queries';
import ProductVariants from '../components/products/ProductVariants';

const SELECTORS = {
  HEADER: 'header.site-header',
  SEARCH_INPUT: '.search-query',
  SEARCH_CLOSE: '.search-close',
  SEARCH_FILTERS: '#SearchContainer .search-results__filter',
  SEARCH_FILTERS_BTN: '#SearchContainer .search-filters__button > button',
  MOBILE: {
    CONTAINER: '.mobile-navbar',
    SEARCH_BTN: 'li[data-open-search="mobile"]',
  },
};

const configRefinementList = {
  sortBy: ['isRefined', 'count:desc'],
  limit: 5,
  showMore: true,
  showMoreLimit: 50,
  templates: {
    item: templates.checkbox,
  },
  cssClasses: {
    showMore: 'text-gradient',
  },
};

const __convertCurrency = (interval, tries) => {
  Currency.customConvertAll();

  if (document.querySelector('.money').dataset.currency !== undefined || tries >= 5) {
    clearInterval(interval);
  }
};

const renderPreactComponents = async () => {
  const container = document.getElementById('search-results');
  if (!container) return;

  const productEls = Array.from(container.querySelectorAll('[data-render="search-product"]'));
  if (productEls.length === 0) return;

  const shopifyIds = productEls.map((el) => `gid://shopify/Product/${el.dataset.shopifyId}`);

  try {
    const resp = await gql(QUERY_PRODUCTS_BY_IDS, { ids: shopifyIds });
    let {
      data: { nodes: products },
    } = await resp.json();
    products = products.filter((p) => p);

    productEls.forEach((el) => {
      const shopifyId = `gid://shopify/Product/${el.dataset.shopifyId}`;
      const product = products.find(({ id }) => id === shopifyId);

      if (product) {
        render(<ProductVariants product={product} />, el.querySelector('[data-component="ProductVariants"]'));
      }
    });

    let tries = 0;
    if (Currency) {
      const interval = setInterval(() => {
        __convertCurrency(interval, (tries += 1));
      }, 50);
    }
  } catch (e) {
    console.error(e);
  }
};

/**
 * Returns a string to identify the current "context".
 * Docs: https://www.algolia.com/doc/guides/managing-results/rules/rules-overview/how-to/customize-search-results-by-platform/?client=javascript
 */
const getSearchContext = () => {
  if (window.SlrsIdentify !== undefined) {
    return 'account';
  }

  return 'guest';
};

class Search {
  constructor() {
    window.ALGOLIA_INDEX = indexName;
    this.firstRender = true;
    this.location = {
      href: window.location.href,
      pathname: window.location.pathname,
    };

    document.addEventListener('DOMContentLoaded', () => {
      const userToken =
        ShopifyAnalytics.lib?.user?.().traits?.().uniqToken ||
        ShopifyAnalytics.meta?.page?.customerId ||
        ShopifyAnalytics.lib.config.initialDocumentCookie.match('_y=(.+?);')[1];

      window.userToken = String(userToken);

      window.algoliainsights = insightsClient(appId, apiKey);

      // focus the input when we're on the search page or programmatically triggered
      if (
        window.__searchTrigger?.fire ||
        (window.location.pathname.includes('/search') && !window.location.search.includes('type=article'))
      ) {
        this.attachListeners({ page: 'search' });

        // support collection pages triggering search
        if (window.__searchTrigger?.options) {
          const searchTriggered = setInterval(() => {
            try {
              if (this.search) {
                const {
                  options: { facets },
                } = window.__searchTrigger;

                if (facets) {
                  facets.forEach(([key, value]) => {
                    this.search.helper.toggleFacetRefinement(key, value);
                  });
                }

                this.search.helper.search();
                clearInterval(searchTriggered);
              }
            } catch (e) {
              console.error(e);
              clearInterval(searchTriggered);
            }
          }, 100);
        }

        // support old search links
        if (window.location.hash) {
          const params = new URLSearchParams(window.location.hash.substring(1));

          if (params.has('q')) {
            const interval = setInterval(() => {
              try {
                if (this.search) {
                  this.search.helper.setQuery(params.get('q')).search();
                  clearInterval(interval);
                }
              } catch (e) {
                console.error(e);
                clearInterval(interval);
              }
            }, 100);
          }
        }

        if (Search.isMobile()) {
          this.toggleSearch(true);
        } else {
          try {
            document.querySelector(SELECTORS.SEARCH_INPUT).focus();
            document.querySelector(SELECTORS.SEARCH_CLOSE).style.display = 'none';
          } catch (e) {
            console.warn('Can NOT load properly setup search: missing elment(s)');
            console.error(e);
          }
        }
      } else {
        this.attachListeners();
      }
    });
  }

  static isMobile() {
    return window.innerWidth < 751;
  }

  toggleSearch(show) {
    const isSearching = document.querySelector('.is-searching');

    if (show) {
      if (!isSearching) {
        document.querySelector('body').classList.add('is-searching');
      }

      if (this.firstRender) {
        this.renderUI();
        this.firstRender = false;
      } else {
        document.querySelectorAll(SELECTORS.SEARCH_INPUT).forEach((inputEl) => {
          inputEl.value = this.search.helper.state.query;
        });
      }
    } else if (isSearching) {
      document.querySelector('body').classList.remove('is-searching');
    }
  }

  static renderSearchBox(renderOptions, isFirstRender) {
    const { query, refine } = renderOptions;

    document.querySelectorAll(SELECTORS.SEARCH_INPUT).forEach((inputEl) => {
      if (isFirstRender) {
        inputEl.value = query;

        inputEl.addEventListener('input', (e) => {
          refine(e.target.value);
        });
      }
    });
  }

  renderUI() {
    const searchClient = algoliasearch(appId, apiKey);

    this.search = instantsearch({
      searchClient,
      indexName,
      routing,
      insights: true,
    });

    window.search = this.search;

    let timerId;
    const customSearchBox = connectSearchBox(Search.renderSearchBox);

    this.search.addWidgets([
      widgets.configure({
        hitsPerPage: 96,
        ruleContexts: [getSearchContext()],
      }),

      customSearchBox({
        queryHook(query, refine) {
          clearTimeout(timerId);

          timerId = setTimeout(() => {
            refine(query);
          }, 500);
        },
      }),

      widgets.hits({
        container: '#search-results',
        cssClasses: {
          root: 'collection__products',
          list: 'collection__products-wrapper',
        },
        templates: {
          item: templates.product,
          empty: (_, { html }) => html`<img
            src="https://cdn.shopify.com/s/files/1/0318/2649/t/11/assets/no-results.svg?v=189511243498056529"
          />`,
        },
      }),

      // Category
      widgets.hierarchicalMenu({
        container: document.querySelector('#filter-category'),
        attributes: ['sub_categories.lvl0', 'sub_categories.lvl1', 'sub_categories.lvl2'],
        sortBy: ['name:asc'],
        limit: 30,
        showMoreLimit: 50,
        showMore: true,
        templates: {
          item: templates.hierarchicalMenuItem,
        },
      }),

      widgets.refinementList({
        container: '#filter-release-status',
        attribute: 'is_preorder',
        sortBy: ['name:asc'],
        templates: {
          item: templates.checkbox,
        },
        transformItems: (items) => {
          return items.map((item) => {
            return {
              ...item,
              label: item.label === 'false' ? 'Released' : 'Pre-Order',
              highlighted: item.highlighted === 'false' ? 'Released' : 'Pre-Order',
            };
          });
        },
      }),

      widgets.refinementList({
        ...configRefinementList,
        container: '#filter-character',
        attribute: 'named_tags.character',
        searchable: true,
        searchableIsAlwaysActive: false,
      }),

      widgets.refinementList({
        ...configRefinementList,
        container: '#filter-franchise',
        attribute: 'named_tags.franchise',
        searchable: true,
        searchableIsAlwaysActive: false,
      }),

      widgets.currentRefinements({
        container: '#refinement-list',
        cssClasses: {
          list: 'filter__tags',
        },
        transformItems: (items) => {
          return items.map((item) => {
            if (item.label === 'is_preorder') {
              const refinements = item.refinements.map((refinement) => {
                return Object.assign(refinement, {
                  label: refinement.label === 'false' ? 'Released' : 'Preorder',
                });
              });
              return Object.assign(item, { refinements });
            }
            return item;
          });
        },
      }),

      widgets.clearRefinements({
        container: '#clear-refinements',
        templates: {
          resetLabel({ hasRefinements }, { html }) {
            return html`<span class="tw-p-2">${hasRefinements ? 'Clear refinements' : 'No refinements'}</span>`;
          },
        },
      }),

      widgets.pagination({
        container: '#search-pagination',
        padding: 2,
        showFirst: false,
        showLast: false,
        cssClasses: {
          list: 'pagination',
          item: 'pagination__item',
          selectedItem: 'pagination__item--selected',
          disabledItem: 'pagination__item--disabled',
          previousPageItem: 'pagination__item--prev',
          nextPageItem: 'pagination__item--next',
        },
      }),
    ]);

    this.search.start();

    this.search.on('render', $.debounce(500, renderPreactComponents));

    this.search.helper.on('change', (e) => {
      const element = document.querySelector('.gradient-border.filter-list');
      const refinements = Object.values(e.state.disjunctiveFacetsRefinements);

      if (refinements.flat().length > 0) {
        element.style.display = null;
      } else {
        element.style.display = 'none';
      }
    });
  }

  attachListeners({ page } = { page: '' }) {
    // open search mobile
    document.querySelector(SELECTORS.MOBILE.SEARCH_BTN).addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleSearch(true);

      const input = document.querySelector(`${SELECTORS.MOBILE.CONTAINER} ${SELECTORS.SEARCH_INPUT}`);
      if (input) {
        input.focus();
      }
    });

    // open search desktop
    document.querySelector(`.header-content ${SELECTORS.SEARCH_INPUT}`).addEventListener('focus', () => {
      this.toggleSearch(true);
    });

    // handle escape key to close the search
    document.querySelectorAll(SELECTORS.SEARCH_INPUT).forEach((input) => {
      input.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') {
          e.target.blur();

          if (page !== 'search') {
            e.target.value = '';
            this.toggleSearch(false);
            window.history.pushState({}, '', this.location.pathname);
          }
        }
      });

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      });
    });

    // handle search close button
    document.querySelectorAll(SELECTORS.SEARCH_CLOSE).forEach((btn) => {
      btn.addEventListener('click', () => {
        if (page !== 'search') {
          this.toggleSearch(false);
          window.history.pushState({}, '', this.location.pathname);
        } else if (Search.isMobile()) {
          window.location.replace(`${window.location.origin}`);
        }

        document.querySelectorAll(SELECTORS.SEARCH_INPUT).forEach((inputEl) => {
          inputEl.value = '';
        });
      });
    });

    // handle filter open button (mobile only)
    document.querySelector(SELECTORS.SEARCH_FILTERS_BTN).addEventListener('click', () => {
      document.querySelector(SELECTORS.SEARCH_FILTERS).classList.toggle('active');
    });
  }
}

export default new Search();
